import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '@/lib/logger';
import { captureException } from '@/lib/sentry';
import { isStaleChunkError, reloadOnceForStaleChunks } from '@/lib/stale-chunk';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  reloading?: boolean;
}

/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the whole app.
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 *
 * Or with custom fallback:
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // A stale chunk after a deploy isn't a real app error — render nothing while
    // we reload to fresh assets instead of flashing the error UI.
    if (isStaleChunkError(error)) {
      return { hasError: false, reloading: true };
    }
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Stale chunk: reload once to recover. If a reload was already attempted (the
    // deploy is genuinely broken), fall through to the normal error UI.
    if (isStaleChunkError(error)) {
      if (reloadOnceForStaleChunks()) return;
      this.setState({ hasError: true, error, reloading: false });
      return;
    }

    // Log error to console in development
    logger.error('ErrorBoundary caught error:', error, errorInfo);

    // Send to Sentry with component stack trace
    captureException(error, {
      errorBoundary: {
        componentStack: errorInfo.componentStack,
      },
    });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    this.setState({ error, errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    // Navigate to home page
    window.location.href = '/';
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    // Reloading to recover from a stale chunk — render nothing to avoid a flash.
    if (this.state.reloading) {
      return null;
    }

    if (this.state.hasError) {
      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div data-testid="error-boundary" className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6">
            <div className="text-center">
              {/* Error Icon */}
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900">
                <svg
                  className="h-6 w-6 text-red-600 dark:text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              {/* Error Title */}
              <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
                Алдаа гарлаа
              </h1>

              {/* Error Message */}
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Уучлаарай, ямар нэг алдаа гарсан байна. Дахин оролдоно уу.
              </p>

              {/* Error Details (Development Only) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-4 text-left">
                  <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                    Алдааны дэлгэрэнгүй
                  </summary>
                  <div className="mt-2 text-xs bg-gray-100 dark:bg-gray-900 p-3 rounded overflow-auto max-h-48">
                    <pre className="text-red-600 dark:text-red-400 whitespace-pre-wrap">
                      {this.state.error.toString()}
                      {'\n\n'}
                      {this.state.errorInfo?.componentStack}
                    </pre>
                  </div>
                </details>
              )}

              {/* Action Buttons */}
              <div className="mt-6 space-y-3">
                <button
                  data-testid="error-retry-btn"
                  onClick={this.handleReload}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  Хуудас дахин ачаалах
                </button>
                <button
                  onClick={this.handleReset}
                  className="w-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  Нүүр хуудас руу буцах
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
